import EventCalendar, { EventCalendarFragment } from '-/Event/EventCalendar';
import { Stack, useLocalSearchParams, useRouter } from 'expo-router';
import { DateTime } from 'luxon';
import { useState } from 'react';
import type { DateData } from 'react-native-calendars';
import { useQuery } from 'urql';
import { queueEventListReset } from '@/client/exchanges/cacheExchange/eventListResetQueue';
import Box from '@/components/UI/Box';
import CheckboxItem from '@/components/UI/CheckboxRow';
import Text from '@/components/UI/Text';
import { graphql } from '@/gql/graphql';
import useEventStore from '@/stores/useEventStore';
import {
  getCalendarDayFromIsoDate,
  getMonthAnchorFromCalendarDay,
} from '@/utils/getCalendarDateAnchors';
import getStartAndEndDate from '@/utils/getStartAndEndDate';

const GetEventDetailScheduleCalendar = graphql(/* GraphQL */ `
  query GetEventDetailScheduleCalendar(
    $startOfMonth: timestamptz!
    $endOfMonth: timestamptz!
    $mode: String!
    $eventId: uuid!
  ) {
    event_date_range(
      args: {
        start: $startOfMonth
        end: $endOfMonth
        event_id: $eventId
        mode: $mode
      }
    ) {
      ...EventCalendar
    }
  }
`, [EventCalendarFragment]);

export default function EventDetailEventsAction() {
  const { dismissAll } = useRouter();
  const { date, eventId } = useLocalSearchParams<{
    date?: string;
    eventId: string;
  }>();

  const day = getCalendarDayFromIsoDate(date);

  const [month, setMonth] = useState<string>(() =>
    getMonthAnchorFromCalendarDay(day),
  );

  const onDayPress = (date: DateData) => {
    if (date.dateString) {
      queueEventListReset({
        datetime: DateTime.fromISO(date.dateString).startOf('day').toISO(),
        parentEventId: eventId,
      });

      dismissAll();
    }
  };

  const dayObj = DateTime.fromISO(day || DateTime.local().toISODate() || '');
  const startOfMonth = DateTime.fromISO(month).startOf('month').toString();
  const endOfMonth = DateTime.fromISO(month).endOf('month').toString();
  const startOfDay = dayObj.startOf('day').toString();
  const endOfDay = dayObj.endOf('day').toString();
  const selected = getStartAndEndDate(startOfDay, endOfDay);
  const hasFilter = useEventStore(
    (state) => state.detailFilters[eventId]?.showMyTeams,
  );

  const [{ data: calendarData }] = useQuery({
    query: GetEventDetailScheduleCalendar,
    variables: {
      startOfMonth,
      endOfMonth,
      mode: hasFilter ? 'direct' : 'all',
      eventId,
    },
    requestPolicy: 'cache-and-network',
  });

  return (
    <>
      <Stack.Screen
        options={{
          title: hasFilter ? 'Filtered Calendar' : 'Calendar',
        }}
      />
      <Box flex>
        <Stack.Screen options={{ title: 'Filter / Jump to date' }} />

        <CheckboxItem
          variant="primary"
          value={!!hasFilter}
          onValueChange={(value) => {
            useEventStore.setState({
              detailFilters: {
                [eventId]: {
                  showMyTeams: value ? true : undefined,
                },
              },
            });
          }}
        >
          <Text>Filter by events you're directly participating in</Text>
        </CheckboxItem>
        <EventCalendar
          initialDate={day || undefined}
          fragment={calendarData?.event_date_range}
          selected={selected}
          disableUnmarkedDays
          onDayPress={onDayPress}
          onVisibleMonthsChange={(date) => {
            const visibleMonth = date[0]?.dateString;

            if (!visibleMonth) {
              return;
            }

            setMonth(getMonthAnchorFromCalendarDay(visibleMonth));
          }}
        />
      </Box>
    </>
  );
}
